import { Popover } from '@headlessui/react';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { DocumentTextIcon as DocumentTextIconSolid } from '@heroicons/react/24/solid';
import { format, formatDistanceToNow, isWithinInterval, isValid } from 'date-fns';
import { useTranslation } from 'react-i18next';

import type { CalendarEvent } from '@/apps/Schedule/stores/useScheduleStore';

import { useNotesStore, type Note } from '@/apps/Notes/stores/useNotesStore';
import { LinkedNotesPopover } from '@/apps/Schedule/components/LinkedNotesPopover';
import { useDateLocale } from '@/common/hooks/useDateLocale';
import { useWidgetLinks } from '@/common/hooks/useWidgetLinks';
import { useToastStore } from '@/common/stores/useToastStore';
import { LINK_TYPES } from '@/common/utils/constants';

interface EventCardProps {
  event: CalendarEvent;
  onAddNoteClick?: (event: CalendarEvent) => void;
  onEditNoteClick?: (note: Note) => void;
  hasNotesWidget?: boolean;
}

export const EventCard = ({
  event,
  onAddNoteClick,
  onEditNoteClick,
  hasNotesWidget = true,
}: EventCardProps) => {
  const { t } = useTranslation();
  const { locale, timeFormat } = useDateLocale();
  const linkedLinks = useWidgetLinks(LINK_TYPES.SCHEDULE_EVENT, event.id);
  const fetchNotesByIds = useNotesStore((state) => state.fetchNotesByIds);
  const showToast = useToastStore((state) => state.showToast);

  const startDate = new Date(event.startDateTime);
  const endDate = new Date(event.endDateTime);
  const datesValid = isValid(startDate) && isValid(endDate);

  const formattedTime =
    !datesValid
      ? t('schedule.timeUnavailable', 'Time unavailable')
      : event.isAllDay
        ? t('schedule.allDay', 'All day')
        : `${format(startDate, timeFormat, { locale })} - ${format(endDate, timeFormat, { locale })}`;

  const timeFromNow =
    datesValid && !event.isAllDay
      ? formatDistanceToNow(startDate, { addSuffix: true, locale })
      : null;

  const isNow =
    datesValid &&
    isWithinInterval(new Date(), {
      start: startDate,
      end: endDate,
    });

  const showNoteButton = true;

  const handleNoteClick = () => {
    if (linkedLinks.length > 0) return;
    if (hasNotesWidget && onAddNoteClick) {
      onAddNoteClick(event);
    } else if (!hasNotesWidget) {
      showToast({
        type: 'info',
        message: t('schedule.addNotesWidgetFirst', 'Add a Notes widget first'),
        duration: 3,
      });
    }
  };

  const getButtonTitle = () => {
    if (!hasNotesWidget) return t('schedule.addNotesWidgetFirst', 'Add a Notes widget first');
    if (linkedLinks.length > 0) {
      return `${linkedLinks.length} note${linkedLinks.length > 1 ? 's' : ''} linked`;
    }
    return t('notes.addNote', 'Add note');
  };

  return (
    <div className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow border border-gray-100">
      <div className="flex justify-between items-start gap-4">
        <div className="grow">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-medium text-gray-800">{event.title}</h3>
            {showNoteButton &&
              (linkedLinks.length > 0 && hasNotesWidget ? (
                <Popover>
                  {({ open }) => (
                    <LinkedNotesPopover
                      fetchNotesByIds={fetchNotesByIds}
                      getButtonTitle={getButtonTitle}
                      hasNotesWidget={hasNotesWidget}
                      linkedLinks={linkedLinks}
                      open={open}
                      onEditNoteClick={onEditNoteClick}
                    />
                  )}
                </Popover>
              ) : (
                <button
                  className="p-1 rounded hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
                  title={getButtonTitle()}
                  onClick={handleNoteClick}
                >
                  {linkedLinks.length > 0 ? (
                    <DocumentTextIconSolid className="w-4 h-4 text-amber-500" />
                  ) : (
                    <DocumentTextIcon className="w-4 h-4" />
                  )}
                </button>
              ))}
          </div>
          <p className="text-sm font-medium text-gray-700">{formattedTime}</p>
          <div>
            {isNow ? (
              <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                Now
              </span>
            ) : (
              timeFromNow && (
                <p className="text-sm text-gray-500 mt-0.5">{timeFromNow}</p>
              )
            )}
          </div>
        </div>
        {event.meetLink && (
          <a
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
            href={event.meetLink}
            rel="noopener noreferrer"
            target="_blank"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
            Join
          </a>
        )}
      </div>
    </div>
  );
};
